import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, Sparkles, CalendarCheck, ShieldCheck, X, Palette, Crown, Info } from "lucide-react";
import { BASES, ADDONS } from "@/config/clinicOS";
import { IncludedEverywhere } from "@/components/landing/IncludedEverywhere";
import { StartPlanModal } from "@/components/landing/StartPlanModal";
import { EnterpriseModal } from "@/components/landing/EnterpriseModal";
import { useAudit } from "@/components/landing/AuditContext";

/* Map an audit answer profile → recommended base. */
function recommendBase({ team, calls, tools }) {
  const heavyTeam  = ["6-15", "16+"].includes(team);
  const heavyCalls = ["300-800", "800+"].includes(calls);
  const heavyTools = ["7-9", "10+"].includes(tools);
  return (heavyTeam || heavyCalls || heavyTools) ? "hub-plus" : "hub";
}

export default function Build() {
  const [params] = useSearchParams();
  const openAudit = useAudit();

  const fromAudit  = params.get("from") === "audit";
  const baseFromUrl = params.get("base"); // "hub" | "hub-plus"

  const profile = useMemo(() => ({
    type:   params.get("type")   || "",
    team:   params.get("team")   || "",
    calls:  params.get("calls")  || "",
    noshow: params.get("noshow") || "",
    tools:  params.get("tools")  || "",
  }), [params]);

  const recommendedBase = useMemo(() => {
    if (fromAudit) return recommendBase(profile);
    if (baseFromUrl === "hub" || baseFromUrl === "hub-plus") return baseFromUrl;
    return null;
  }, [fromAudit, profile, baseFromUrl]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalBase, setModalBase] = useState("hub");
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);

  const handleStartWithBase = (baseId) => {
    setModalBase(baseId);
    setModalOpen(true);
  };

  return (
    <section
      id="build-page"
      data-testid="build-page"
      className="min-h-[100svh] pb-20 pt-16 sm:pt-32 sm:pb-24"
    >
      {/* ─────────── ① HERO ─────────── */}
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-3xl"
        >
          {fromAudit ? (
            <span
              data-testid="build-audit-eyebrow"
              className="inline-flex items-center gap-2 rounded-full border border-violet-500/30 bg-gradient-to-r from-violet-500/15 to-blue-500/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-violet-600 dark:text-violet-300"
            >
              <Sparkles className="h-3 w-3" />
              Pre-picked from your 60-second audit
            </span>
          ) : (
            <span className="hidden sm:inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
              Start your clinic
            </span>
          )}

          <h1 className="mt-0 sm:mt-5 font-display text-[2.05rem] font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            Start your clinic on Clinic OS.{" "}
            <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
              $297 or $497/mo per location.
            </span>
          </h1>

          <p className="mt-3 sm:mt-6 max-w-2xl text-[14px] leading-relaxed text-muted-foreground sm:text-lg lg:text-xl">
            Pick the base that fits where your clinic is today. Add-ons can wait
            until your business actually needs them — no upfront commitment.
          </p>

          {/* Trust strip — desktop only; mobile pricing note covers the same ground */}
          <div className="mt-6 hidden flex-wrap items-center gap-x-5 gap-y-2 text-xs font-medium text-muted-foreground sm:flex">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Cancel anytime
            </span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span>No setup fee</span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span>No long-term contracts</span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span>Live in &lt; 14 days</span>
          </div>

          {/* Prominent pricing transparency block */}
          <div
            data-testid="build-pricing-note"
            className="mt-4 sm:mt-6 flex items-start gap-2.5 rounded-2xl border border-blue-500/25 bg-gradient-to-br from-blue-500/[0.05] to-violet-500/[0.04] p-3.5 text-[12.5px] leading-relaxed sm:p-5 sm:text-[13px]"
          >
            <Info className="mt-0.5 h-4 w-4 flex-none text-blue-500" />
            <p className="text-foreground/85">
              <span className="font-bold text-foreground">Heads up — pricing.</span>{" "}
              Your base subscription is fixed at{" "}
              <span className="font-bold text-foreground">$297 (Hub)</span> or{" "}
              <span className="font-bold text-foreground">$497 (Hub+)</span>{" "}
              per location. Add-on pricing is custom — quoted based on your specialty and consumption{" "}
              <span className="font-semibold text-foreground">after a 20-min discovery call</span>. No surprises, no contact-us gates.
            </p>
          </div>
        </motion.div>
      </div>

      {/* ─────────── ② UNLIMITED EVERYTHING — promoted ABOVE the bases (hidden on mobile to surface bases sooner) ─────────── */}
      <div className="mt-12 hidden sm:mt-16 sm:block">
        <IncludedEverywhere />
      </div>

      {/* ─────────── ③ TWO BASE CARDS ─────────── */}
      <div className="mx-auto mt-10 max-w-7xl px-5 sm:mt-24 sm:px-8 lg:px-12" id="pick-a-base">
        <div className="max-w-3xl">
          <p className="hidden sm:inline-flex items-center gap-2 rounded-full border border-violet-500/30 bg-violet-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-300">
            <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
            Pick your base · one of two
          </p>
          <h2 className="mt-0 sm:mt-4 font-display text-[1.65rem] font-black leading-[1.06] tracking-tight sm:text-4xl lg:text-5xl">
            Two plans. One simple decision.
          </h2>
          <p className="mt-2 sm:mt-3 max-w-2xl text-[13.5px] leading-relaxed text-muted-foreground sm:text-base">
            All the unlimited-everything basics above ship with both. Pick the plan that fits today.
          </p>
        </div>

        <div className="mt-10 grid gap-5 lg:grid-cols-2 lg:gap-6">
          {BASES.map((b) => {
            const isFeatured = b.id === "hub-plus";
            const isRecommended = recommendedBase === b.id;
            const Icon = b.icon;
            return (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5 }}
                data-testid={`build-base-card-${b.id}`}
                className={`group relative flex flex-col overflow-hidden rounded-2xl border p-5 transition-all ${
                  isFeatured
                    ? "border-violet-500/40 bg-gradient-to-br from-violet-500/[0.12] via-blue-500/[0.05] to-transparent shadow-lg shadow-violet-500/10"
                    : "border-border bg-card hover:border-foreground/25"
                } ${isRecommended ? "ring-2 ring-offset-2 ring-offset-background ring-violet-500/60" : ""}`}
              >
                {/* glow */}
                <div className={`pointer-events-none absolute -right-20 -top-20 h-40 w-40 rounded-full ${isFeatured ? "bg-violet-500/25" : "bg-blue-500/15"} blur-3xl`} />

                {/* corner ribbons */}
                <div className="absolute right-4 top-4 flex flex-col items-end gap-1.5">
                  {isRecommended && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-violet-600 to-blue-600 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.12em] text-white shadow-md shadow-violet-600/25">
                      <Sparkles className="h-2.5 w-2.5" />
                      Recommended
                    </span>
                  )}
                  {isFeatured && !isRecommended && (
                    <span className="inline-flex items-center rounded-md bg-violet-500 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.12em] text-white">
                      Most popular
                    </span>
                  )}
                </div>

                {/* Top row — icon + name + price (compact) */}
                <div className="relative flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gradient-to-br ${b.color} text-white shadow-md ring-1 ring-inset ring-white/15`}>
                      <Icon className="h-[18px] w-[18px]" />
                    </span>
                    <div>
                      <h3 className="font-display text-xl font-black tracking-tight sm:text-2xl">{b.name}</h3>
                      <p className="text-[12.5px] leading-snug text-muted-foreground">{b.tagline}</p>
                    </div>
                  </div>
                </div>

                <div className="relative mt-4 flex items-baseline gap-2">
                  <span className="font-display text-4xl font-black tracking-[-0.03em]">{b.priceLabel}</span>
                  <span className="font-display text-sm font-bold text-muted-foreground">/mo</span>
                  <span className="text-[11px] text-muted-foreground">· per location · flat</span>
                </div>

                <ul className="relative mt-4 grid grid-cols-1 gap-1.5">
                  {b.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[12.5px] leading-snug text-foreground/85">
                      <Check className={`mt-0.5 h-3.5 w-3.5 flex-none ${isFeatured ? "text-violet-500" : "text-blue-500"}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {b.replaces?.length > 0 && (
                  <div className="relative mt-3 flex flex-wrap items-center gap-1.5">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80">Replaces</span>
                    {b.replaces.map((r) => (
                      <span key={r} className="inline-flex items-center rounded-full border border-border bg-secondary/40 px-2 py-0.5 text-[10.5px] font-semibold text-foreground/75">
                        <X className="mr-1 h-2.5 w-2.5 text-rose-500" />
                        {r}
                      </span>
                    ))}
                  </div>
                )}

                <div className="relative mt-4 flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => handleStartWithBase(b.id)}
                    data-testid={`build-base-cta-${b.id}`}
                    className={`group/btn inline-flex h-10 items-center justify-center gap-2 rounded-xl text-[13px] font-bold transition-all hover:-translate-y-0.5 ${
                      isFeatured
                        ? "bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-md shadow-violet-600/25 hover:shadow-lg"
                        : "bg-foreground text-background hover:bg-foreground/90 hover:shadow-md"
                    }`}
                  >
                    <CalendarCheck className="h-3.5 w-3.5" />
                    Start with {b.short}
                    <ArrowRight className="h-3 w-3 transition-transform group-hover/btn:translate-x-0.5" />
                  </button>
                  <Link
                    to={`/products/${b.id}`}
                    data-testid={`build-base-learn-${b.id}`}
                    className="inline-flex items-center justify-center gap-1 text-[11.5px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                  >
                    See full {b.short} details
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* More options row — Whitelabel & Enterprise side-by-side */}
        <div className="mt-6 grid gap-4 lg:grid-cols-2 lg:gap-5">
          {/* Whitelabel option */}
          <button
            type="button"
            onClick={() => handleStartWithBase("hub-plus")}
            data-testid="build-whitelabel-option"
            className="group relative overflow-hidden rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-500/[0.10] via-fuchsia-500/[0.05] to-transparent p-5 text-left transition-all hover:-translate-y-0.5 hover:border-violet-500/55 hover:shadow-md hover:shadow-violet-500/15"
          >
            <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-violet-500/20 blur-3xl" />
            <div className="relative flex items-start gap-4">
              <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-md shadow-violet-500/30">
                <Palette className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-600 dark:text-violet-300">
                  Whitelabel option
                </p>
                <p className="mt-1 font-display text-[17px] font-black tracking-tight">
                  Make it yours <span className="font-display text-base font-bold text-muted-foreground">· +$297/mo</span>
                </p>
                <p className="mt-1 text-[12px] leading-snug text-muted-foreground">
                  Run Clinic OS under your own brand. One workspace · multiple accounts · billed per workspace.
                </p>
              </div>
              <ArrowRight className="mt-1 h-4 w-4 flex-none text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-foreground" />
            </div>
          </button>

          {/* Enterprise option */}
          <button
            type="button"
            onClick={() => setEnterpriseOpen(true)}
            data-testid="build-enterprise-option"
            className="group relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.10] via-orange-500/[0.05] to-transparent p-5 text-left transition-all hover:-translate-y-0.5 hover:border-amber-500/55 hover:shadow-md hover:shadow-amber-500/15"
          >
            <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-amber-500/20 blur-3xl" />
            <div className="relative flex items-start gap-4">
              <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 text-white shadow-md shadow-amber-500/30">
                <Crown className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.18em] text-amber-600 dark:text-amber-400">
                  Enterprise plan
                </p>
                <p className="mt-1 font-display text-[17px] font-black tracking-tight">
                  Multi-location & groups <span className="font-display text-base font-bold text-muted-foreground">· custom</span>
                </p>
                <p className="mt-1 text-[12px] leading-snug text-muted-foreground">
                  Full suite + dedicated CSM + custom integrations + whitelabel. Custom-quoted after discovery.
                </p>
              </div>
              <ArrowRight className="mt-1 h-4 w-4 flex-none text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-foreground" />
            </div>
          </button>
        </div>
      </div>

      {/* ─────────── ④ ADD-ONS (DE-EMPHASIZED) ─────────── */}
      <div className="mx-auto mt-20 max-w-7xl px-6 sm:mt-24 sm:px-8 lg:px-12">
        <div className="max-w-3xl">
          <p className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            When you&apos;re ready to grow
          </p>
          <h2 className="mt-4 font-display text-3xl font-black leading-[1.06] tracking-tight sm:text-4xl">
            Snap-on modules, anytime.
          </h2>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-muted-foreground sm:text-base">
            Don&apos;t add what you don&apos;t need. Turn modules on from your dashboard
            the moment your clinic is ready — no sales call, no setup gates.
          </p>
        </div>

        <div className="mt-8 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {ADDONS.map((m) => {
            const Icon = m.icon;
            return (
              <Link
                key={m.id}
                to={`/products/${m.id}`}
                data-testid={`build-addon-${m.id}`}
                className="group flex items-start gap-3 rounded-2xl border border-border bg-card/50 p-4 transition-all hover:-translate-y-0.5 hover:border-foreground/25 hover:bg-card hover:shadow-sm"
              >
                <span className={`inline-flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-gradient-to-br ${m.color} text-white shadow-sm ring-1 ring-inset ring-white/15`}>
                  <Icon className="h-[17px] w-[17px]" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-[14px] font-bold tracking-tight">{m.name}</p>
                  <p className="mt-0.5 truncate text-[12px] leading-snug text-muted-foreground">{m.tagline}</p>
                </div>
                <ArrowRight className="mt-1 h-3.5 w-3.5 flex-none text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-foreground" />
              </Link>
            );
          })}
        </div>

        <p className="mt-6 flex flex-wrap items-center gap-2 text-[12.5px] text-muted-foreground">
          <Check className="h-3.5 w-3.5 text-emerald-500" />
          Add-on pricing is custom — we&apos;ll quote based on your specialty and consumption after a 20-min discovery call. No &ldquo;contact us&rdquo; gates.
        </p>
      </div>

      {/* ─────────── ⑤ FINAL CTA ─────────── */}
      <div className="mt-20 sm:mt-24">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden rounded-3xl border border-violet-500/30 bg-gradient-to-br from-violet-600 via-blue-600 to-violet-500 p-8 text-white shadow-xl shadow-violet-600/25 sm:p-12">
            <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-fuchsia-500/40 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-blue-400/30 blur-3xl" />
            <div className="relative max-w-2xl">
              <p className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.22em] text-white/95 backdrop-blur">
                <Sparkles className="h-3 w-3" />
                14-day go-live · personal onboarding
              </p>
              <h2 className="mt-5 font-display text-3xl font-black leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl">
                Ready when you are.
              </h2>
              <p className="mt-3 text-base leading-relaxed text-white/85 sm:text-lg">
                Book a 20-minute walkthrough — we&apos;ll show your exact stack on a real clinic,
                quote your monthly cost, and get you live within 2 weeks.
              </p>
              <div className="mt-7 flex flex-wrap gap-2.5">
                <button
                  type="button"
                  onClick={() => handleStartWithBase("hub")}
                  data-testid="build-final-cta-hub"
                  className="inline-flex h-12 items-center gap-2 rounded-xl bg-white px-5 text-[13.5px] font-bold text-foreground shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg"
                >
                  Start with Hub · $297/mo per location
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleStartWithBase("hub-plus")}
                  data-testid="build-final-cta-hub-plus"
                  className="inline-flex h-12 items-center gap-2 rounded-xl bg-foreground px-5 text-[13.5px] font-bold text-background shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg"
                >
                  Start with Hub+ · $497/mo per location
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="mt-4 text-[12px] text-white/70">
                Not sure? Take the{" "}
                <button
                  type="button"
                  onClick={openAudit}
                  className="font-bold text-white underline-offset-2 hover:underline"
                >
                  60-second audit
                </button>
                {" "}— we&apos;ll suggest the right base for free.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {modalOpen && (
        <StartPlanModal baseId={modalBase} onClose={() => setModalOpen(false)} />
      )}
      {enterpriseOpen && (
        <EnterpriseModal onClose={() => setEnterpriseOpen(false)} />
      )}
    </section>
  );
}
